import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  useCreateInvoiceDraft,
  useIssueInvoice,
  useRecordPayment,
  useSendInvoiceEmail,
  type InvoiceRow,
} from '@/hooks/useInvoices';
import { useRequestClinioPayLink, type RequestPayLinkOutput } from '@/hooks/useClinicPay';
import { usePayWithPunchCard } from '@/hooks/usePunchCardPayment';
import { appointmentDetailQueryKey } from '@/hooks/useAppointments';
import { fetchAppointmentInvoiceLinks } from '@/hooks/useAppointmentInvoicing';
import { hasValidPatientEmail } from '@/lib/calendarPaymentChoice';
import { invoiceIsReusableForSettlement } from '@/lib/appointmentSettlement';
import { isPaymentRequestMethod } from '@/lib/manualPaymentMethods';
import type { Database } from '@/types/database.generated';

type InvoiceStatus = Database['public']['Enums']['invoice_status'];

export type MonetaryInvoiceMethod = 'invoice' | 'mobilepay' | 'card' | 'cash' | 'bank';

export class StaffPaymentChoiceError extends Error {
  readonly code: StaffPaymentErrorCode;

  constructor(code: StaffPaymentErrorCode, message: string) {
    super(message);
    this.name = 'StaffPaymentChoiceError';
    this.code = code;
  }
}

export type StaffPaymentErrorCode =
  | 'missing_email'
  | 'invoice_create_failed'
  | 'invoice_issue_failed'
  | 'invoice_email_failed'
  | 'mobilepay_failed'
  | 'card_failed'
  | 'payment_link_failed'
  | 'desk_payment_failed'
  | 'klip_failed'
  | 'already_paid'
  | 'already_settled'
  | 'invoice_already_issued'
  | 'invoice_paid_locked';

export interface ExistingAppointmentInvoice {
  id: string;
  status: InvoiceStatus;
  payment_method: string | null;
}

export interface InvoiceEmailResult {
  invoice: InvoiceRow;
  emailed: boolean;
  recipient: string;
}

export interface ClinioPayResult {
  invoice: InvoiceRow;
  pay: RequestPayLinkOutput;
  paymentMethod: 'card' | 'mobilepay' | 'payment_link';
}

export interface DeskPaymentResult {
  invoice: InvoiceRow | null;
  paymentMethod: string;
  prepaid?: boolean;
}

async function findExistingAppointmentInvoice(
  appointmentId: string,
): Promise<ExistingAppointmentInvoice | null> {
  const link = (await fetchAppointmentInvoiceLinks([appointmentId])).get(appointmentId);
  if (!link || !invoiceIsReusableForSettlement(link.invoice_status)) return null;
  return {
    id: link.invoice_id,
    status: link.invoice_status,
    payment_method: link.payment_method,
  };
}

async function findExistingPunchUsage(appointmentId: string): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from('punch_card_usages')
    .select('id')
    .eq('appointment_id', appointmentId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

async function assertCanStartNewSettlement(appointmentId: string): Promise<void> {
  if (await findExistingPunchUsage(appointmentId)) {
    throw new StaffPaymentChoiceError(
      'already_settled',
      'Appointment is already settled with punch card',
    );
  }
  const existing = await findExistingAppointmentInvoice(appointmentId);
  if (!existing) return;

  switch (existing.status) {
    case 'draft':
    case 'void':
      return;
    case 'paid':
    case 'partial':
      throw new StaffPaymentChoiceError('already_paid', 'Appointment invoice is already paid');
    case 'issued':
    case 'overdue':
    case 'expired_pay_link':
    case 'cancelled':
    case 'refunded':
      throw new StaffPaymentChoiceError(
        'invoice_already_issued',
        'Appointment already has an issued invoice',
      );
    default: {
      const _exhaustive: never = existing.status;
      return _exhaustive;
    }
  }
}

async function loadInvoice(invoiceId: string): Promise<InvoiceRow> {
  const { data, error } = await supabase.from('invoices').select('*').eq('id', invoiceId).single();
  if (error) throw error;
  return data as InvoiceRow;
}

export function useStaffAppointmentPayment() {
  const queryClient = useQueryClient();
  const createInvoiceDraft = useCreateInvoiceDraft();
  const issueInvoice = useIssueInvoice();
  const sendInvoiceEmail = useSendInvoiceEmail();
  const requestPayLink = useRequestClinioPayLink();
  const recordPayment = useRecordPayment();
  const payWithPunchCard = usePayWithPunchCard();

  function invalidateFinance(patientId: string, appointmentId: string) {
    queryClient.invalidateQueries({ queryKey: ['invoices'] });
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['calendar', 'invoices'] });
    queryClient.invalidateQueries({ queryKey: appointmentDetailQueryKey(appointmentId) });
    queryClient.invalidateQueries({ queryKey: ['punch-cards'] });
    queryClient.invalidateQueries({ queryKey: ['punch-card-usages'] });
    queryClient.invalidateQueries({ queryKey: ['appointments', 'patient', patientId] });
  }

  const ensureIssuedInvoice = useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      appointmentId: string;
    }): Promise<InvoiceRow> => {
      const existing = await findExistingAppointmentInvoice(input.appointmentId);

      let invoice: InvoiceRow;
      if (existing && (existing.status === 'paid')) {
        throw new StaffPaymentChoiceError('already_paid', 'Appointment invoice is already paid');
      }
      if (
        existing &&
        (existing.status === 'issued' ||
          existing.status === 'partial' ||
          existing.status === 'overdue' ||
          existing.status === 'expired_pay_link')
      ) {
        return loadInvoice(existing.id);
      }
      if (!existing || !invoiceIsReusableForSettlement(existing.status)) {
        try {
          invoice = await createInvoiceDraft.mutateAsync({
            clinic_id: input.clinicId,
            patient_id: input.patientId,
            appointment_id: input.appointmentId,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Invoice creation failed';
          if (message.includes('already settled with punch card')) {
            throw new StaffPaymentChoiceError('already_settled', message);
          }
          throw new StaffPaymentChoiceError('invoice_create_failed', message);
        }
      } else if (existing.status === 'draft') {
        invoice = { id: existing.id, status: existing.status } as InvoiceRow;
      } else {
        return loadInvoice(existing.id);
      }

      if (invoice.status === 'draft') {
        try {
          invoice = await issueInvoice.mutateAsync(invoice.id);
        } catch (error) {
          throw new StaffPaymentChoiceError(
            'invoice_issue_failed',
            error instanceof Error ? error.message : 'Invoice issue failed',
          );
        }
      }

      return loadInvoice(invoice.id).catch(() => invoice);
    },
  });

  const sendInvoiceByEmail = useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      appointmentId: string;
      patientEmail: string | null | undefined;
    }): Promise<InvoiceEmailResult> => {
      if (!hasValidPatientEmail(input.patientEmail)) {
        throw new StaffPaymentChoiceError('missing_email', 'Patient has no valid email');
      }
      const recipient = input.patientEmail!.trim();
      const invoice = await ensureIssuedInvoice.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        appointmentId: input.appointmentId,
      });

      try {
        await sendInvoiceEmail.mutateAsync({
          invoice_id: invoice.id,
          recipient,
        });
      } catch (error) {
        throw new StaffPaymentChoiceError(
          'invoice_email_failed',
          error instanceof Error ? error.message : 'Invoice email failed',
        );
      }

      return { invoice, emailed: true, recipient };
    },
    onSuccess: (_result, variables) => {
      invalidateFinance(variables.patientId, variables.appointmentId);
    },
  });

  const sendClinioPay = useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      appointmentId: string;
      paymentMethod: 'card' | 'mobilepay' | 'payment_link';
    }): Promise<ClinioPayResult> => {
      const invoice = await ensureIssuedInvoice.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        appointmentId: input.appointmentId,
      });

      const failCode: StaffPaymentErrorCode =
        input.paymentMethod === 'card'
          ? 'card_failed'
          : input.paymentMethod === 'mobilepay'
            ? 'mobilepay_failed'
            : 'payment_link_failed';
      let pay: RequestPayLinkOutput;
      try {
        pay = await requestPayLink.mutateAsync(
          input.paymentMethod === 'payment_link'
            ? invoice.id
            : { invoiceId: invoice.id, paymentMethod: input.paymentMethod },
        );
      } catch (error) {
        throw new StaffPaymentChoiceError(
          failCode,
          error instanceof Error ? error.message : 'Payment request failed',
        );
      }

      return { invoice, pay, paymentMethod: input.paymentMethod };
    },
    onSuccess: (_result, variables) => {
      invalidateFinance(variables.patientId, variables.appointmentId);
    },
  });

  const recordDeskPayment = useMutation({
    mutationFn: async (input: {
      clinicId: string;
      patientId: string;
      appointmentId: string;
      paymentMethod: string;
      amountCents?: number;
      paymentDate?: string;
    }): Promise<DeskPaymentResult> => {
      if (isPaymentRequestMethod(input.paymentMethod)) {
        throw new StaffPaymentChoiceError(
          'desk_payment_failed',
          'MobilePay is requested through Clinio Pay and is not recorded as received',
        );
      }
      if ((input.amountCents ?? 0) > 0) {
        const { error: prepaidError } = await supabase.rpc('record_appointment_prepayment', {
          p_appointment_id: input.appointmentId,
          p_amount_cents: input.amountCents ?? 0,
          p_payment_method: input.paymentMethod,
          p_received_at: input.paymentDate,
        });
        if (!prepaidError) {
          return { invoice: null, paymentMethod: input.paymentMethod, prepaid: true };
        }
      }
      const invoice = await ensureIssuedInvoice.mutateAsync({
        clinicId: input.clinicId,
        patientId: input.patientId,
        appointmentId: input.appointmentId,
      });
      const full = await loadInvoice(invoice.id);
      const outstanding = Math.max(0, (full.total_cents ?? 0) - (full.total_paid_cents ?? 0));
      if (outstanding <= 0 || full.status === 'paid') {
        throw new StaffPaymentChoiceError('already_paid', 'Appointment invoice is already paid');
      }
      const amountCents = input.amountCents ?? outstanding;
      if (amountCents <= 0 || amountCents > outstanding) {
        throw new StaffPaymentChoiceError('desk_payment_failed', 'Payment amount is not valid');
      }
      try {
        await recordPayment.mutateAsync({
          invoice_id: full.id,
          amount_cents: amountCents,
          payment_date: input.paymentDate,
          payment_method: input.paymentMethod,
        });
      } catch (error) {
        throw new StaffPaymentChoiceError(
          'desk_payment_failed',
          error instanceof Error ? error.message : 'Could not record payment',
        );
      }
      return { invoice: full, paymentMethod: input.paymentMethod };
    },
    onSuccess: (_result, variables) => {
      invalidateFinance(variables.patientId, variables.appointmentId);
    },
  });

  const redeemKlip = useMutation({
    mutationFn: async (input: {
      punchCardId: string;
      appointmentId: string;
      patientId: string;
      clinicId: string;
      clipsToUse?: number;
    }) => {
      try {
        await assertCanStartNewSettlement(input.appointmentId);
        return await payWithPunchCard.mutateAsync({
          punch_card_id: input.punchCardId,
          appointment_id: input.appointmentId,
          patient_id: input.patientId,
          clinic_id: input.clinicId,
          clips_to_use: input.clipsToUse ?? 1,
        });
      } catch (error) {
        if (error instanceof StaffPaymentChoiceError) throw error;
        const message = error instanceof Error ? error.message : 'Klip redemption failed';
        if (message.includes('already paid') || message.includes('already settled')) {
          throw new StaffPaymentChoiceError('already_paid', message);
        }
        if (message.includes('already has an issued invoice')) {
          throw new StaffPaymentChoiceError('invoice_already_issued', message);
        }
        throw new StaffPaymentChoiceError('klip_failed', message);
      }
    },
    onSuccess: (_result, variables) => {
      invalidateFinance(variables.patientId, variables.appointmentId);
    },
  });

  const replaceInvoiceWithKlip = useMutation({
    mutationFn: async (input: {
      punchCardId: string;
      appointmentId: string;
      patientId: string;
      clinicId: string;
      clipsToUse?: number;
    }) => {
      const { data, error } = await supabase.rpc(
        'replace_unpaid_appointment_invoice_with_punch_card' as never,
        {
          p_punch_card_id: input.punchCardId,
          p_appointment_id: input.appointmentId,
          p_clips_to_use: input.clipsToUse ?? 1,
        } as never,
      );
      if (error) {
        const message = error.message ?? 'Klip redemption failed';
        if (message.includes('cannot be replaced without refund')) {
          throw new StaffPaymentChoiceError('invoice_paid_locked', message);
        }
        if (message.includes('already paid') || message.includes('already settled')) {
          throw new StaffPaymentChoiceError('already_settled', message);
        }
        if (message.includes('already has an issued invoice')) {
          throw new StaffPaymentChoiceError('invoice_already_issued', message);
        }
        throw new StaffPaymentChoiceError('klip_failed', message);
      }
      return data;
    },
    onSuccess: (_result, variables) => {
      invalidateFinance(variables.patientId, variables.appointmentId);
    },
  });

  return {
    sendInvoiceByEmail,
    sendClinioPay,
    sendMobilePay: sendClinioPay,
    recordDeskPayment,
    redeemKlip,
    replaceInvoiceWithKlip,
    isPending:
      sendInvoiceByEmail.isPending ||
      sendClinioPay.isPending ||
      recordDeskPayment.isPending ||
      redeemKlip.isPending ||
      replaceInvoiceWithKlip.isPending ||
      ensureIssuedInvoice.isPending,
  };
}
